"use client";

/**
 * Step 4: Review & Confirmation
 * Final review before submitting booking
 */

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import type { BookingData } from "@/types/booking-v2";
import type { ServiceInfo } from "@/types/tenant-landing-v2";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertCircle,
  Calendar,
  Clock,
  Copy,
  CreditCard,
  Edit2,
  Mail,
  MessageSquare,
  Phone,
  User,
} from "lucide-react";
import { toast } from "sonner";

interface ReviewStepProps {
  bookingData: BookingData;
  services: ServiceInfo[];
  tenantPixKey: string | null;
  onEdit: (step: number) => void;
  onFeePaymentConfirmChange: (confirmed: boolean) => void;
}

export function ReviewStep({
  bookingData,
  services,
  tenantPixKey,
  onEdit,
  onFeePaymentConfirmChange,
}: ReviewStepProps) {
  const selectedServices = services.filter((s) =>
    bookingData.selectedServices.includes(s.id)
  );

  const servicesWithFee = selectedServices.filter((s) => s.requiresBookingFee);
  const hasBookingFee = servicesWithFee.length > 0;
  const totalBookingFee = servicesWithFee.reduce(
    (sum, s) => sum + (s.bookingFeeAmount || 0),
    0
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  return (
    <div className="space-y-4 h-full flex flex-col overflow-hidden">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto -mx-4 md:mx-0 px-4 md:px-0 overscroll-contain">
        <div className="space-y-4 pb-4">
          {/* Services */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-base">Serviços Selecionados</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(1)}
                className="h-8"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Editar
              </Button>
            </div>
            <div className="space-y-2">
              {selectedServices.map((service) => (
                <div
                  key={service.id}
                  className="flex items-start justify-between gap-2"
                >
                  <div className="flex-1">
                    <p className="font-medium">{service.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(service.duration)}
                    </div>
                  </div>
                  <p className="font-semibold whitespace-nowrap">
                    {service.hasVariablePrice
                      ? "Sob consulta"
                      : formatCurrency(service.price / 100)}
                  </p>
                </div>
              ))}
              <Separator className="my-3" />
              <div className="flex items-center justify-between font-semibold">
                <span>Total</span>
                <div className="text-right">
                  <p>{formatDuration(bookingData.totalDuration)}</p>
                  <p className="text-primary">
                    {formatCurrency(bookingData.totalPrice / 100)}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Date and Time */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-base">Data e Horário</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(2)}
                className="h-8"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Editar
              </Button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <p className="font-medium">
                  {bookingData.selectedDate &&
                    format(
                      bookingData.selectedDate,
                      "EEEE, d 'de' MMMM 'de' yyyy",
                      {
                        locale: ptBR,
                      }
                    )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <p className="font-medium">{bookingData.selectedTime}</p>
              </div>
            </div>
          </Card>

          {/* Customer Info */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-base">Seus Dados</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(3)}
                className="h-8"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Editar
              </Button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <p>{bookingData.customer.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <p>{bookingData.customer.phone}</p>
              </div>
              {bookingData.customer.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <p className="break-all">{bookingData.customer.email}</p>
                </div>
              )}
              {bookingData.customer.notes && (
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    {bookingData.customer.notes}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Booking Fee Section - Destacado */}
          {hasBookingFee && (
            <Card className="border-2 border-primary bg-gradient-to-br from-primary/5 via-primary/3 to-transparent">
              <div className="p-5 space-y-4">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-primary mb-1">
                      Taxa de Reserva Obrigatória
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Necessária para garantir seu horário
                    </p>
                  </div>
                </div>

                {/* Fee Amount */}
                <div className="bg-background rounded-lg p-4 border-2 border-primary/20">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      Valor da Taxa:
                    </span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(totalBookingFee / 100)}
                    </span>
                  </div>

                  <Separator className="my-3" />

                  {/* PIX Key Section */}
                  {tenantPixKey && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Chave PIX:</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(tenantPixKey);
                            toast.success("Chave PIX copiada!");
                          }}
                          className="h-8"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copiar
                        </Button>
                      </div>
                      <div className="bg-muted rounded-md p-3 font-mono text-sm break-all">
                        {tenantPixKey}
                      </div>
                    </div>
                  )}
                </div>

                {/* Important Info */}
                <div className="bg-blue-50 dark:bg-blue-950/50 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-blue-900 dark:text-blue-100">
                        ⚠️ Informações Importantes:
                      </p>
                      <ul className="space-y-1.5 text-blue-800 dark:text-blue-200">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 dark:text-blue-400 font-bold">
                            •
                          </span>
                          <span>
                            <strong>Esta taxa NÃO é um valor adicional</strong>{" "}
                            - será descontada do valor total do serviço
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 dark:text-blue-400 font-bold">
                            •
                          </span>
                          <span>
                            Serve apenas para{" "}
                            <strong>garantir sua reserva</strong> e evitar
                            ausências
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 dark:text-blue-400 font-bold">
                            •
                          </span>
                          <span>
                            O restante (
                            {formatCurrency(
                              (bookingData.totalPrice - totalBookingFee) / 100
                            )}
                            ) será pago diretamente no estabelecimento
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Confirmation Checkbox */}
                <div className="bg-background rounded-lg border-2 border-primary/30 p-4">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <Checkbox
                      id="fee-payment-confirmation"
                      checked={bookingData.feePaymentConfirmed ?? false}
                      onCheckedChange={(checked) =>
                        onFeePaymentConfirmChange(checked === true)
                      }
                      className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <div className="flex-1 select-none">
                      <p className="font-semibold text-sm group-hover:text-primary transition-colors">
                        Confirmo que realizei o pagamento da taxa de reserva via
                        PIX
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Você precisa confirmar o pagamento para finalizar o
                        agendamento
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </Card>
          )}

          {/* Final notice */}
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-900 dark:text-green-100">
              ✅ Ao confirmar, você receberá uma mensagem com todos os detalhes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
